import React, { useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';

import { SearchForm } from '@shared/components/SearchForm';
import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { FilterChip } from '@shared/components/FilterChip';

import { useStyles } from './UserExecutions.styles'

import { useExecutionsGridUIStore } from '@core/useStores';

import { messages } from 'NeoAnalytics/neoAnalytics.messages'
import { ThemeProps } from '@styles/theme';
import { getExecutionStatusMeta } from 'NeoAnalytics/executionSteps';
import { ExecutionsList } from '../ExecutionsList';

const STATUS_LABELS: Record<string, string> = {
    completed: 'Completed',
    failed: 'Failed',
    aborted: 'Stopped',
    abandoned: 'Abandoned',
    ongoing: 'In Progress',
};

type ExecutionFilters = {
    platforms: string[];
    modes: string[];
    statuses: string[];
};

const EMPTY_FILTERS: ExecutionFilters = { platforms: [], modes: [], statuses: [] };

export const UserExecutions: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const executionsGridUIStore = useExecutionsGridUIStore();

    const [localSearchText, setLocalSearchText] = useState('');
    const [activeFilters, setActiveFilters] = useState<ExecutionFilters>(EMPTY_FILTERS);
    const [pendingFilters, setPendingFilters] = useState<ExecutionFilters>(EMPTY_FILTERS);
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);

    const onExecutionsSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {return;}
        setLocalSearchText(searchText);
    };

    const openFiltersModal = () => {
        setPendingFilters(activeFilters);
        setFiltersModalOpen(true);
    };

    const applyFilters = () => {
        setActiveFilters(pendingFilters);
        setFiltersModalOpen(false);
    };

    const togglePending = (field: 'platforms' | 'modes' | 'statuses', value: string) => {
        setPendingFilters(prev => {
            const list = prev[field];

            return {
                ...prev,
                [field]: list.includes(value) ? list.filter(v => v !== value) : [...list, value],
            };
        });
    };

    const removeFilter = (field: 'platforms' | 'modes' | 'statuses', value: string) => {
        setActiveFilters(prev => ({
            ...prev,
            [field]: prev[field].filter(v => v !== value),
        }));
    };

    const clearAllFilters = () => setActiveFilters(EMPTY_FILTERS);

    const anyFilterActive =
        activeFilters.platforms.length > 0 || activeFilters.modes.length > 0 || activeFilters.statuses.length > 0;

    return (
        <>
            <Observer>
                {() => {
                    const allExecutions = executionsGridUIStore.executions.slice();
                    const uniquePlatforms = Array.from(
                        new Set(allExecutions.map(e => e.platform).filter((p): p is string => Boolean(p)))
                    );
                    const uniqueModes = Array.from(
                        new Set(allExecutions.map(e => e.executionMode).filter((m): m is string => Boolean(m)))
                    );
                    const uniqueStatuses = Array.from(
                        new Set(allExecutions.map(e => getExecutionStatusMeta(e, theme).state))
                    );

                    return (
                        <div className={styles.mainWrapper}>
                            <div className={styles.header}>
                                <label className={styles.contentTitle}>
                                    Sessions
                                </label>
                                <div className={styles.filters}>
                                    <SearchForm
                                        initialValues={{ search: '' }}
                                        onSearchTextChanged={onExecutionsSearchTextChanged}
                                        className={styles.searchWrapper}
                                    />
                                    <span
                                        style={{ display: 'inherit' }}
                                        title={messages['neoAnalyticsPage.filter.title']}
                                    >
                                        <FilterListOutlinedIcon
                                            onClick={openFiltersModal}
                                            className={styles.filterIcon}
                                        />
                                    </span>
                                </div>
                            </div>

                            {anyFilterActive && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', paddingBottom: 8, flex: '0 0 auto' }}>
                                    {activeFilters.platforms.map(p => (
                                        <FilterChip key={p} text={`Platform: ${p}`} onRemove={() => removeFilter('platforms', p)} />
                                    ))}
                                    {activeFilters.modes.map(m => (
                                        <FilterChip key={m} text={`Mode: ${m}`} onRemove={() => removeFilter('modes', m)} />
                                    ))}
                                    {activeFilters.statuses.map(s => (
                                        <FilterChip key={s} text={`Status: ${STATUS_LABELS[s] ?? s}`} onRemove={() => removeFilter('statuses', s)} />
                                    ))}
                                    <p
                                        onClick={clearAllFilters}
                                        style={{ cursor: 'pointer', color: theme.colors.violet_primary, fontFamily: theme.font.secondary, fontSize: 16, margin: '4px 0' }}
                                    >
                                        Clear all
                                    </p>
                                </div>
                            )}

                            <div className={styles.tableWrapper}>
                                <ExecutionsList
                                    searchText={localSearchText}
                                    localFilters={activeFilters}
                                />
                            </div>

                            <Modal
                                isOpen={filtersModalOpen}
                                onRequestClose={() => setFiltersModalOpen(false)}
                            >
                                <div style={{ padding: 24, minWidth: 300 }}>
                                    <h3 style={{ marginTop: 0, marginBottom: 16, fontFamily: theme.font.primary, color: theme.colors.text_heading }}>
                                        Filter Sessions
                                    </h3>

                                    {uniquePlatforms.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <p style={{ fontFamily: theme.font.secondary, fontWeight: 700, marginBottom: 8, color: theme.colors.text_heading }}>
                                                Platform
                                            </p>
                                            {uniquePlatforms.map(p => (
                                                <FormControlLabel
                                                    key={p}
                                                    label={<span style={{ fontFamily: theme.font.secondary, color: theme.colors.text_heading }}>{p}</span>}
                                                    control={
                                                        <Checkbox
                                                            checked={pendingFilters.platforms.includes(p)}
                                                            onChange={() => togglePending('platforms', p)}
                                                            sx={{ color: theme.colors.violet_primary, '&.Mui-checked': { color: theme.colors.violet_primary } }}
                                                        />
                                                    }
                                                    style={{ display: 'flex' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {uniqueModes.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <p style={{ fontFamily: theme.font.secondary, fontWeight: 700, marginBottom: 8, color: theme.colors.text_heading }}>
                                                Execution Mode
                                            </p>
                                            {uniqueModes.map(m => (
                                                <FormControlLabel
                                                    key={m}
                                                    label={<span style={{ fontFamily: theme.font.secondary, color: theme.colors.text_heading }}>{m}</span>}
                                                    control={
                                                        <Checkbox
                                                            checked={pendingFilters.modes.includes(m)}
                                                            onChange={() => togglePending('modes', m)}
                                                            sx={{ color: theme.colors.violet_primary, '&.Mui-checked': { color: theme.colors.violet_primary } }}
                                                        />
                                                    }
                                                    style={{ display: 'flex' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {uniqueStatuses.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <p style={{ fontFamily: theme.font.secondary, fontWeight: 700, marginBottom: 8, color: theme.colors.text_heading }}>
                                                Status
                                            </p>
                                            {uniqueStatuses.map(s => (
                                                <FormControlLabel
                                                    key={s}
                                                    label={<span style={{ fontFamily: theme.font.secondary, color: theme.colors.text_heading }}>{STATUS_LABELS[s] ?? s}</span>}
                                                    control={
                                                        <Checkbox
                                                            checked={pendingFilters.statuses.includes(s)}
                                                            onChange={() => togglePending('statuses', s)}
                                                            sx={{ color: theme.colors.violet_primary, '&.Mui-checked': { color: theme.colors.violet_primary } }}
                                                        />
                                                    }
                                                    style={{ display: 'flex' }}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                                        <Button
                                            color="secondary"
                                            onClick={() => setFiltersModalOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={applyFilters}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            </Modal>
                        </div>
                    );
                }}
            </Observer>
        </>
    );
}